import React, { useState, useEffect } from 'react';
import { FaMedal, FaBook, FaFileAlt, FaClock, FaChartLine } from 'react-icons/fa';
import SectionHeader from '../components/SectionHeader/SectionHeader';
import { getCurrentBookInfo, getBooksVisualization, formatToK } from '../config/booksConfig';
import StatisticsMobile from './StatisticsMobile';

export default function ModernStatistics({ statistics = null, period = 'global', onPeriodChange, loading = false, context = 'user' }) {
  const [animatedValues, setAnimatedValues] = useState({});
  const [isSmallScreen, setIsSmallScreen] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    try { return window.matchMedia('(max-width: 500px)').matches; } catch (_) { return false; }
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    let mq;
    try {
      mq = window.matchMedia('(max-width: 500px)');
      const handler = (e) => setIsSmallScreen(!!e.matches);
      // Newer & older browser support
      if (mq.addEventListener) {
        mq.addEventListener('change', handler);
      } else if (mq.addListener) {
        mq.addListener(handler);
      }
      // Set initial
      setIsSmallScreen(!!mq.matches);
      return () => {
        try {
          if (mq.removeEventListener) mq.removeEventListener('change', handler);
          else if (mq.removeListener) mq.removeListener(handler);
        } catch (_) {}
      };
    } catch (_) {}
  }, []);
  
  // Default statistics for demo if none provided
  const defaultStats = {
    percentile: 0,
    charactersGenerated: 0,
    reportsGenerated: 0,
    averageReportsPerDay: 0
  };
  
  const stats = statistics || defaultStats;
  const currentBook = getCurrentBookInfo(stats.charactersGenerated || 0);
  const booksVisualization = getBooksVisualization(stats.charactersGenerated || 0);
  
  // Animation effect
  useEffect(() => {
    if (!loading) {
      // Animate numbers
      const targets = {
        percentile: stats.percentile || 0,
        characters: stats.charactersGenerated || 0,
        reports: stats.reportsGenerated || 0,
        reportsProgress: (stats.reportsProgress || 0),
        timeProgress: (stats.timeProgress || 0),
        avgReports: (stats.averageReportsPerDay || 0)
      };
      
      Object.keys(targets).forEach(key => {
        let start = 0;
        const end = targets[key];
        const duration = 1500;
        const startTime = Date.now();
        
        const animate = () => {
          const now = Date.now();
          const progress = Math.min((now - startTime) / duration, 1);
          const current = Math.floor(progress * end);
          
          setAnimatedValues(prev => ({ ...prev, [key]: current }));
          
          if (progress < 1) {
            requestAnimationFrame(animate);
          }
        };
        
        requestAnimationFrame(animate);
      });
    }
  }, [loading, stats]);

  // Render mobile variant on small screens
  if (isSmallScreen) {
    return (
      <StatisticsMobile
        statistics={statistics}
        period={period}
        onPeriodChange={onPeriodChange}
        loading={loading}
      />
    );
  }
  
  const styles = {
    container: {
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: '20px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '16px'
    },
    title: {
      fontSize: '20px',
      fontWeight: '600',
      background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-danger) 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      margin: 0
    },
    periodToggle: {
      display: 'flex',
      background: '#f3f4f6',
      borderRadius: '10px',
      padding: '3px',
      gap: '2px'
    },
    periodBtn: {
      padding: '8px 16px',
      border: 'none',
      background: 'transparent',
      color: '#6b7280',
      fontWeight: '600',
      cursor: 'pointer',
      borderRadius: '7px',
      transition: 'all 0.3s ease',
      fontSize: '13px'
    },
    periodBtnActive: {
      background: 'white',
      color: 'var(--color-primary)',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '20px'
    },
    card: {
      background: 'white',
      borderRadius: '16px',
      padding: '20px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
      border: '1px solid #f0f0f0',
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden'
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '12px'
    },
    label: {
      fontSize: '13px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: '#6b7280'
    },
    iconWrapper: {
      width: '42px',
      height: '42px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '20px'
    },
    mainValue: {
      display: 'flex',
      alignItems: 'baseline',
      gap: '6px',
      marginBottom: '12px'
    },
    number: {
      fontSize: '32px',
      fontWeight: '700',
      color: '#1f2937'
    },
    unit: {
      fontSize: '16px',
      color: '#6b7280',
      fontWeight: '500'
    },
    subtitle: {
      fontSize: '12px',
      color: '#9ca3af',
      marginTop: '4px'
    },
    // Normal distribution styles
    distributionChart: {
      height: '60px',
      marginTop: '12px',
      position: 'relative'
    },
    userPosition: {
      position: 'absolute',
      bottom: 0,
      width: '2px',
      height: '100%',
      background: 'linear-gradient(180deg, var(--color-warning), var(--color-orange))',
      transform: 'translateX(-50%)',
      animation: 'pulse 2s infinite'
    },
    userDot: {
      position: 'absolute',
      bottom: '-4px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '8px',
      height: '8px',
      background: 'linear-gradient(135deg, var(--color-warning), var(--color-orange))',
      borderRadius: '50%',
      boxShadow: '0 0 0 3px rgba(251, 191, 36, 0.2)'
    },
    // Books visualization
    booksVisual: {
      display: 'flex',
      gap: '4px',
      marginTop: '12px',
      marginBottom: '8px'
    },
    bookItem: {
      width: '20px',
      height: '28px',
      background: '#f3f4f6',
      borderRadius: '2px',
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.3s ease'
    },
    bookFill: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      transition: 'height 0.5s ease'
    },
    bookProgressText: {
      fontSize: '11px',
      color: '#6b7280',
      marginTop: '4px'
    },
    // Circular progress
    circularProgress: {
      position: 'relative',
      width: '80px',
      height: '80px'
    },
    circularValue: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -75%)',
      fontSize: '18px',
      fontWeight: '700',
      color: '#1f2937'
    },
    circularLabel: {
      position: 'absolute',
      top: '65%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      fontSize: '9px',
      color: '#6b7280',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap'
    },
    contentWithCircle: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start'
    },
    leftContent: {
      flex: 1
    },
    avgPerDayPill: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '6px',
      background: 'linear-gradient(135deg, var(--color-green), var(--color-success))',
      color: 'white',
      padding: '10px 14px',
      borderRadius: '12px',
      minWidth: '120px',
      justifyContent: 'center',
      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)'
    },
    avgLabel: {
      fontSize: '11px',
      fontWeight: '700',
      opacity: 0.9,
      letterSpacing: '0.3px'
    },
    avgValue: {
      fontSize: '22px',
      fontWeight: '800',
      lineHeight: 1
    },
    avgSuffix: {
      fontSize: '12px',
      fontWeight: '600',
      opacity: 0.95
    },
    timeDisplay: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center',
      marginTop: '4px'
    },
    timeBlock: {
      textAlign: 'center'
    },
    timeValue: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1f2937'
    },
    timeLabel: {
      fontSize: '10px',
      color: '#6b7280',
      textTransform: 'uppercase',
      marginTop: '2px'
    }
  };
  
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Chargement des statistiques...</p>
        </div>
      </div>
    );
  }
  
  const headerTitle = context === 'enterprise' ? "Statistiques de l'organisation" : 'Vos statistiques';

  return (
    <div style={styles.container}>
      <div style={styles.header}>
      <SectionHeader icon={FaChartLine} title={headerTitle} variant="warning" titleGradient={true} />

      </div>
      
      <div style={styles.grid}>
        {/* Percentile Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.label}>Classement</div>
            <div style={{ ...styles.iconWrapper, background: 'linear-gradient(135deg, var(--color-warning), var(--color-orange))' }}>
              <FaMedal style={{ color: 'white' }} />
            </div>
          </div>
          <div style={styles.mainValue}>
            <span style={styles.number}>{animatedValues.percentile || 0}</span>
            <span style={styles.unit}>ᵉ percentile</span>
          </div>
          <div style={styles.distributionChart}>
            <svg style={{ width: '100%', height: '100%' }} viewBox="0 0 300 60">
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#fbbf24', stopOpacity: 0.6 }} />
                  <stop offset="100%" style={{ stopColor: '#f59e0b', stopOpacity: 0.1 }} />
                </linearGradient>
              </defs>
              <path fill="url(#gradient)" d="M 0,60 Q 75,10 150,10 T 300,60 Z" />
              <path fill="none" stroke="#e5e7eb" strokeWidth="2" d="M 0,60 Q 75,10 150,10 T 300,60" />
            </svg>
            <div style={{ ...styles.userPosition, left: `${stats.percentile}%` }}>
              <div style={styles.userDot}></div>
            </div>
          </div>
        </div>
        
        {/* Characters/Books Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.label}>Caractères générés</div>
            <div style={{ ...styles.iconWrapper, background: 'linear-gradient(135deg, var(--color-indigo), var(--color-purple))' }}>
              <FaBook style={{ color: 'white' }} />
            </div>
          </div>
          <div style={styles.mainValue}>
            <span style={styles.number}>{formatToK(animatedValues.characters || 0)}</span>
            <span style={styles.unit}>caractères</span>
          </div>
          <div style={styles.booksVisual}>
            {booksVisualization.map((book, index) => (
              <div key={index} style={styles.bookItem}>
                {(book.filled || book.partial) && (
                  <div
                    style={{
                      ...styles.bookFill,
                      height: `${book.fillPercent}%`,
                      background: book.color
                    }}
                  />
                )}
              </div>
            ))}
          </div>
          <div style={styles.bookProgressText}>
            {currentBook.progressPercent}% {currentBook.name}
            {currentBook.bookNumber > 1 && ` #${currentBook.bookNumber}`}
          </div>
        </div>
        
        {/* Reports Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.label}>Rapports générés</div>
            <div style={{ ...styles.iconWrapper, background: 'linear-gradient(135deg, var(--color-green), var(--color-success))' }}>
              <FaFileAlt style={{ color: 'white' }} />
            </div>
          </div>
          <div style={styles.contentWithCircle}>
            <div style={styles.leftContent}>
              <div style={styles.mainValue}>
                <span style={styles.number}>{(animatedValues.reports || 0).toLocaleString()}</span>
                <span style={styles.unit}>rapports</span>
              </div>
            </div>
            <div style={styles.avgPerDayPill}>
              <div style={styles.avgLabel}>Moyenne</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                <span style={styles.avgValue}>{animatedValues.avgReports || 0}</span>
                <span style={styles.avgSuffix}>/ jour</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Time Saved Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.label}>Temps économisé</div>
            <div style={{ ...styles.iconWrapper, background: 'linear-gradient(135deg, var(--color-danger), #dc2626)' }}>
              <FaClock style={{ color: 'white' }} />
            </div>
          </div>
          <div style={styles.contentWithCircle}>
            <div style={styles.leftContent}>
              <div style={styles.timeDisplay}>
                {(stats.months || 0) > 0 && (
                  <div style={styles.timeBlock}>
                    <div style={styles.timeValue}>{stats.months}</div>
                    <div style={styles.timeLabel}>mois</div>
                  </div>
                )}
                {((stats.months || 0) > 0 || (stats.days || 0) > 0) && (
                  <div style={styles.timeBlock}>
                    <div style={styles.timeValue}>{stats.remainingDays}</div>
                    <div style={styles.timeLabel}>jours</div>
                  </div>
                )}
                <div style={styles.timeBlock}>
                  <div style={styles.timeValue}>{stats.remainingHours || 0}</div>
                  <div style={styles.timeLabel}>heures</div>
                </div>
              </div>
              <div style={styles.subtitle}>~{(stats.totalHours || 0).toLocaleString()} heures totales</div>
            </div>
            <div style={styles.circularProgress}>
              <svg width="80" height="80" style={{ transform: 'rotate(-90deg)' }}>
                <circle
                  cx="40" cy="40" r="35"
                  fill="none" stroke="#f3f4f6" strokeWidth="6"
                />
                <circle
                  cx="40" cy="40" r="35"
                  fill="none" stroke="url(#redGradient)" strokeWidth="6"
                  strokeLinecap="round"
                  strokeDasharray="220"
                  strokeDashoffset={220 - (220 * (animatedValues.timeProgress || 0) / 100)}
                  style={{ transition: 'stroke-dashoffset 1.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
                />
                <defs>
                  <linearGradient id="redGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#f43f5e', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: '#dc2626', stopOpacity: 1 }} />
                  </linearGradient>
                </defs>
              </svg>
              <div style={styles.circularValue}>{Math.round(animatedValues.timeProgress || 0)}%</div>
              <div style={styles.circularLabel}>efficacité</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}